import { ILocation, ILocationTreeCountry } from "@interfaces";

/**
 * Derive the lists shown in each level of the location picker from the
 * cached `allLocations` tree (returned by FT
 * `GET /products/home/locations`), given the user's current selections.
 *
 * Returns a shape compatible with the existing picker UI: `id` is a
 * string, `text` is the display label, `isAny` is always false (the
 * picker prepends its own "Any" sentinel for the State/Region/Locality
 * rows).
 *
 * `hasStatesLayer` is false for countries that go country -> regions
 * directly (e.g. Cambodia, Costa Rica). The picker uses this to skip
 * rendering the State row entirely for those countries.
 */
export const deriveLocationLists = (
    allLocations: ILocationTreeCountry[],
    selected: {
        countryId?: string | null;
        stateId?: string | null;
        regionId?: string | null;
    },
): {
    countries: ILocation[];
    states: ILocation[];
    regions: ILocation[];
    localities: ILocation[];
    hasStatesLayer: boolean;
} => {
    const toLocation = (n: { id: number; text: string }): ILocation => ({
        id: String(n.id),
        text: n.text,
        isAny: false,
    });

    const countries = allLocations.map(toLocation);

    const country = selected.countryId
        ? allLocations.find((c) => String(c.id) === String(selected.countryId))
        : undefined;

    const states = (country?.states ?? []).map(toLocation);
    const hasStatesLayer = Boolean(country?.states && country.states.length > 0);

    let regions: ILocation[] = [];
    if (country) {
        if (hasStatesLayer) {
            const st = (country.states ?? []).find(
                (s) => String(s.id) === String(selected.stateId),
            );
            regions = (st?.regions ?? []).map(toLocation);
        } else {
            regions = (country.regions ?? []).map(toLocation);
        }
    }

    let localities: ILocation[] = [];
    if (country && selected.regionId) {
        const regionPool = hasStatesLayer
            ? (country.states ?? []).flatMap((s) => s.regions ?? [])
            : (country.regions ?? []);
        const region = regionPool.find(
            (r) => String(r.id) === String(selected.regionId),
        );
        localities = (region?.locations ?? []).map(toLocation);
    }

    return { countries, states, regions, localities, hasStatesLayer };
};
